// © Microsoft Corporation. All rights reserved.

import {
    ProductType,
    Subscription,
    TransactionHistoryItem,
    getSubscriptions,
    getTransactionHistory,
    renderCreateSubscription,
    renderManageSubscription,
    unlinkPublisher
} from "@msnews/subscription-sdk-wrapper";
import { arrow_down, credit_card, renew } from "../../utils/icons";

import { OutlineButton } from "@microsoft/fast-components-react-msft";
import React from "react";
import { SocialProps } from "../../Social.props";
import { String } from "typescript-string-operations";
import { User } from "@msnews/social-data-service";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { getExternalURL } from "../../utils/urlHelper";
import { getXPayToken } from "@msnews/social-data-service";
import { hasThemeBase } from "../../utils/themeHelpers";

export interface ProfileSubscriptionsProps extends SocialProps {
    user: User;
    publishers: {[publisherId: string]: User};
}

export interface ProfileSubscriptionsState {
    subscriptions: Subscription[];
    expandedSubscriptions: Set<Subscription>;
    transactions: {[subscriptionId: string]: TransactionHistoryItem[]};
    transactionFetchErrors: {[subscriptionId: string]: boolean};
    subscriptionsFetchFailed?: boolean;
}

export default class ProfileSubscriptions extends React.Component<ProfileSubscriptionsProps, ProfileSubscriptionsState> {
    subscriptions: Subscription[] = [];
    constructor(props) {
        super(props);
        this.state = this.getInitialState();
    }

    getInitialState(): ProfileSubscriptionsState {
        return {
            subscriptions: [],
            expandedSubscriptions: new Set(),
            transactions: {},
            transactionFetchErrors: {}
        };
    }

    isSubscriptionActive(subscription: Subscription) {
        return subscription.subscriptionStatus === "Active";
    }

    getUtcDate(timestring) {
        // Explicitly add time-zone info if it does not exist
        timestring = timestring.includes("Z") ? timestring : timestring+"Z";
        return new Date(timestring);
    }

    isSubscriptionPrepaid(subscription: Subscription) {
        // TODO: Learn how to understand if a subscription is prepaid and implement it
        return false;
    }

    getPaymentMethodString(paymentInfoString: string) {
        const paymentInfo = JSON.parse(paymentInfoString);
        const cardType = paymentInfo.cardType.charAt(0).toUpperCase() + paymentInfo.cardType.slice(1);
        return `${cardType} **${paymentInfo.last4CardNumber}`;
    }

    async loadSubscriptions() {
        const { fetchUser } = this.props.usersConnector;
        const subscriptionsResponse = await getSubscriptions({ authToken: await getXPayToken() });
        if (subscriptionsResponse.status) {
            subscriptionsResponse.data.forEach(async subscription => {
                fetchUser(subscription.publisherIdFromMsn);
                const transactionResponse = await getTransactionHistory({
                    subscriptionId: subscription.subscriptionId,
                    authToken: await getXPayToken()
                });
                if (transactionResponse.status) {
                    this.setState(state => ({
                        transactions: {...state.transactions, [subscription.subscriptionId]: transactionResponse.data}
                    }));
                } else {
                    this.setState(state => ({
                        transactionFetchErrors: {...state.transactionFetchErrors, [subscription.subscriptionId]: true}
                    }));
                }
            });
            this.setState(() => ({ subscriptions: subscriptionsResponse.data }));
        } else {
            this.setState(() => ({ subscriptionsFetchFailed: true }));
        }
    }

    async reloadSubscriptions() {
        this.setState(() => this.getInitialState());
        this.loadSubscriptions();
    }

    componentDidMount() {
        this.loadSubscriptions();
    }

    render() {
        const { responsiveClass, managedClasses, t, theme, publishers } = this.props;
        const { subscriptions, subscriptionsFetchFailed } = this.state;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);

        if (!subscriptions || !publishers) { return; }

        if (subscriptionsFetchFailed) {
            return (
                <div className={managedClasses.profileSubscriptionsContainer}>
                    <p className="cwt-subscriptions-fetch-error">{t.ProfilePageSubscriptionsFetchError}</p>
                </div>
            );
        }

        const activeSubscriptions = subscriptions.filter((subs) =>subs && this.isSubscriptionActive(subs));
        const expiredSubscriptions = subscriptions.filter((subs) => subs && !this.isSubscriptionActive(subs));

        const activeSubscriptionsTitle = t.ProfilePageSubscriptionsActiveSubscriptionsTitle;
        const expiredSubscriptionsTitle = t.ProfilePageSubscriptionsExpiredSubscriptionsTitle;

        return (
            <div className={
                classNames(managedClasses.profileSubscriptionsContainer,
                    [managedClasses.profileSubscriptionsSuperAppContainer, isSuperApp],
                    responsiveClass)}
            >
                { this.renderSubscriptionHistoryCard(activeSubscriptionsTitle, activeSubscriptions) }
                { expiredSubscriptions && expiredSubscriptions.length ? this.renderSubscriptionHistoryCard(expiredSubscriptionsTitle, expiredSubscriptions) : null }
            </div>
        );
    }

    renderSubscriptionHistoryCard(title: string, subscriptions: Subscription[]) {
        const { managedClasses } = this.props;
        return this.state.subscriptions && (
            <div className={classNames(managedClasses.subscriptionHistoryContainer)}>
                <div className="subs-history-title">
                    { title }
                </div>
                <div className="subs-history-body">
                    {/* Use index as key for now, because all publishers are the same currently, the mock publisher */}
                    { subscriptions && subscriptions.length ? subscriptions.map((subs, index) => this.renderSingleSubscription(subs, index)) : this.renderEmptyState()}
                </div>
            </div>
        );
    }

    renderEmptyState() {
        const { t } = this.props;
        return <div className="subs-empty-state-container">
            <span className="empty-state-text"> { t.ProfilePageSubscriptionsNoSubscription } </span>
        </div>;
    }

    renderSingleSubscription(subscription: Subscription, index: number) {
        const { responsiveClass, managedClasses, publishers, t } = this.props;
        const { transactions, transactionFetchErrors } = this.state;

        const publisher = publishers[subscription.publisherIdFromMsn];
        if (!publisher) { return; }

        let actionButtonType: string;
        if (!this.isSubscriptionActive(subscription) && !subscription.isSyncedFromPublisher) {
            actionButtonType = "renew";
        } else if (!this.isSubscriptionActive(subscription) && subscription.isSyncedFromPublisher) {
            actionButtonType = "delink";
        } else if (this.isSubscriptionActive(subscription)) {
            actionButtonType = "manage";
        }

        return this.state.subscriptions && (
            <div key={index} className={classNames(managedClasses.subscriptionContainer, responsiveClass)}>
                <div className="subs-body">
                    <div className="subs-image-container">
                        <img className="publisher-profile-img" src={publisher.primaryAvatar} />
                    </div>
                    <div className="publisher-info-container">
                        <div className="publisher-name">
                            { publisher.primaryName }
                        </div>
                        <a className="publisher-address" href={publisher.otherProperties.siteUrl}>
                            { publisher.otherProperties.siteUrl }
                        </a>
                    </div>
                    <div className="action-button-container">
                        { this.renderActionButton(actionButtonType, publisher, subscription) }
                    </div>
                </div>
                <div className="subs-footer">
                    <span className="footer-tag"> { this.calculateFooterTag(subscription)} </span>
                    { this.isSubscriptionActive(subscription) && <span className="payment-info">
                        { this.calculatePaymentInfo(subscription) }
                    </span> }
                    <div className="footer-action">
                        { this.renderFooterAction(subscription) }
                    </div>
                </div>
                <div className={classNames(
                    "subs-transactions",
                    ["transactions-expanded", this.state.expandedSubscriptions.has(subscription)],
                    ["transactions-collapsed", !this.state.expandedSubscriptions.has(subscription)]
                )}>
                    { transactionFetchErrors[subscription.subscriptionId] ?
                        <div className={"transaction-container"}>
                            <p>{t.ProfilePageSubscriptionsTransactionFetchError}</p>
                        </div> :
                        transactions[subscription.subscriptionId] && transactions[subscription.subscriptionId].map((trans, index) => (
                            <div key={index} className="transaction-container">
                                <i className="cwt-icon">{credit_card}</i>
                                <div className="transaction-text-container">
                                    { String.Format(
                                        t.ProfilePageSubscriptionsTransactionInfo,
                                        {paymentAmount: `${trans.paidAmount}$`, paymentDate: this.getUtcDate(trans.paymentDateTimeUtc).toLocaleDateString()}
                                    ) }
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>
        );
    }

    renderActionButton(actionButtonType, publisher, subscription: Subscription) {
        const { t, me, market } = this.props;

        let actionButtonIcon: JSX.Element;
        let actionButtonText: string;
        let action: () => Promise<any>;
        if (actionButtonType === "renew") {
            actionButtonIcon = renew;
            actionButtonText= t.ProfilePageSubscriptionsRenewButtonTitle;
            action = async () => renderCreateSubscription({
                publisherId: subscription.publisherIdFromMsn,
                publisherName: publisher.primaryName,
                publisherLogoUrl: publisher.primaryAvatar,
                productType: ProductType.Premium,
                userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
                locale: market,
                authToken: await getXPayToken(),
                userProfileImageUrl: me.primaryAvatar,
                scenario: "feed",
                onSuccess: () => this.reloadSubscriptions()
            });
        } else if (actionButtonType === "manage") {
            actionButtonText = t.ProfilePageSubscriptionsManageButtonTitle;
            action = async () => renderManageSubscription({
                publisherId: subscription.publisherIdFromMsn,
                publisherName: publisher.primaryName,
                publisherLogoUrl: publisher.primaryAvatar,
                userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
                locale: market,
                authToken: await getXPayToken(),
                userProfileImageUrl: me.primaryAvatar,
                onSuccess: () => this.reloadSubscriptions()
            });
        } else if (actionButtonType === "delink") {
            actionButtonText = t.ProfilePageSubscriptionsDelinkButtonTitle;
            action = async () => {
                const success = await unlinkPublisher({
                    publisherId: subscription.publisherIdFromMsn,
                    authToken: await getXPayToken()
                });
                if (success) {
                    this.reloadSubscriptions();
                }
            };
        }
        return  <OutlineButton
            beforeContent={() => actionButtonIcon && <i className="cwt-icon">{actionButtonIcon}</i> }
            onClick={action}
            className={classNames("button-active")}
        >
            { actionButtonText }
        </OutlineButton>;
    }

    renderFooterAction(subscription: Subscription) {
        const { t, publishers } = this.props;
        const { transactions } = this.state;
        const isExpanded = this.state.expandedSubscriptions.has(subscription);
        const subscriptionId = subscription.subscriptionId;
        const publisher = publishers[subscription.publisherIdFromMsn];

        const transactionAction = transactions[subscriptionId] && transactions[subscriptionId].length ? <div onClick={(e) => this.toggleExpand(subscription)} className="transaction-history-trigger">
            <span className="cwt-title"> {t.ProfilePageSubscriptionsTransactionsHistory} </span>
            <i className={classNames("cwt-icon", ["cwt-icon-rotated", isExpanded])}>{arrow_down}</i>
        </div> : null;
        return !this.isSubscriptionActive(subscription) && subscription.isSyncedFromPublisher ?
            <a href={"https://" + publisher.otherProperties.siteUrl}> {t.ProfilePageSubscriptionsVisitSite} </a> : transactionAction;
    }

    calculateFooterTag(subscription: Subscription) {
        const { t, publishers } = this.props;
        const publisher = publishers[subscription.publisherIdFromMsn];

        const expirationDate = this.getUtcDate(subscription.expireDateUtc).toLocaleDateString();
        if (this.isSubscriptionActive(subscription)) {
            return this.isSubscriptionPrepaid(subscription) ? String.Format(t.ProfilePageSubscriptionsFooterTagPrepaidPayment, {expirationDate}) : String.Format(t.ProfilePageSubscriptionsFooterTagPeriodicPayment, {price: `${subscription.chargeAmount}$`, period: subscription.chargeCycle.toLowerCase()});
        } else {
            return subscription.isSyncedFromPublisher ? String.Format(t.ProfilePageSubscriptionsFooterTagManagedByPublisher, {publisherName: publisher.primaryName}) :  String.Format(t.ProfilePageSubscriptionsFooterTagExpired, {expirationDate});
        }
    }

    toggleExpand(key) {
        const newExpandedSubscriptions = new Set(this.state.expandedSubscriptions);
        if (this.state.expandedSubscriptions.has(key)) {
            newExpandedSubscriptions.delete(key);
        } else {
            newExpandedSubscriptions.add(key);
        }
        this.setState({expandedSubscriptions: newExpandedSubscriptions});

    }

    calculatePaymentInfo(subscription: Subscription) {
        const { t } = this.props;
        const nextChargeDate = this.getUtcDate(subscription.nextChargeDateUtc).toLocaleDateString();
        const paymentMethod = this.getPaymentMethodString(subscription.paymentInfo);
        return this.isSubscriptionPrepaid(subscription) ? String.Format(t.ProfilePageSubscriptionsFooterPaymentInfoPrepaid, {paymentMethod}) : String.Format(t.ProfilePageSubscriptionsFooterPaymentInfoNextCharge, {nextChargeDay: nextChargeDate, paymentMethod});
    }
}